import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Theme, Box } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment-timezone';
import Container from 'components/Container';
import { LoadingData } from 'services/types';
import Typography from '@mui/material/Typography';
import Spinner from 'components/Spinner';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { ReportGetButton } from './components/ReportGetButton';
import { ReportData } from './types';
import Report from './Report';
import CustomAccountingGroups from './components/CustomAccountingGroups';
import HasFeature from 'components/features/HasFeature';

import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import { Routes } from 'routing';

export const AccountingReport = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const [startDate, setStartDate] = useState<moment.Moment | null>(null);
	const [endDate, setEndDate] = useState<moment.Moment | null>(null);
	const [report, setReport] = useState<LoadingData<ReportData | null>>({
		loading: false,
		error: null,
		data: null,
	});

	const handleReportDataChange = (reportData: LoadingData<ReportData>) => {
		setReport(reportData);
	};

	const handleDatesChange = ({
		startDate,
		endDate,
	}: {
		startDate: moment.Moment | null;
		endDate: moment.Moment | null;
	}) => {
		setStartDate(startDate);
		setEndDate(endDate);
	};

	const renderReport = (report: LoadingData<ReportData | null>) => {
		if (report.loading) {
			return (
				<div>
					<div className={classes.spinnerContainer}>
						<Spinner />
					</div>
					<Typography variant="body1" align="center" gutterBottom>
						{t('reports.accounting.loadingReport', 'Loading report')}
						{'...'}
					</Typography>
				</div>
			);
		}
		if (report.error) {
			return (
				<Typography variant="body1" align="center">
					{report.error}
				</Typography>
			);
		}
		if (!report.data || !startDate || !endDate) {
			return null;
		}
		return <Report report={report.data} startDate={startDate} endDate={endDate} />;
	};

	return (
		<>
			<SetPageTitle priority={2} title={t('account.accounting.pageTitle', 'Accounting')} />
			<SetPageNavAction
				priority={2}
				backAction={{ to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }}
				quickLinks={[]}
			/>
			<Container>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
					<ReportGetButton
						startDate={startDate}
						endDate={endDate}
						onDatesChange={handleDatesChange}
						onReportDataChange={handleReportDataChange}
						shopId={shopId}
					/>
					<HasFeature requiredFeature="CUSTOM_ACCOUNTING_GROUPS">
						<CustomAccountingGroups />
					</HasFeature>
				</Box>
				{renderReport(report)}
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		spinnerContainer: {
			width: '100%',
			height: 100,
			position: 'relative',
		},
	}),
);

export default AccountingReport;
